import { useEffect, useRef, useState } from 'react';
import { Alert, LoadingBlock } from '../components/ui.tsx';
import { WorkPage, WorkSection, WorkEmpty } from '../components/work-page.tsx';
import { useTranslation } from 'react-i18next';
import { api } from '../api/client.ts';
import { useSession } from '../state/session.tsx';
import type { OfferableVisit, VisitOfferItem, VisitOfferListing } from '../../shared/staff-visit-offers.ts';

export const visitOfferCopy = {
  en: {
    title: 'Visit coverage',
    back: 'Staff',
    refresh: 'Refresh',
    retry: 'Retry submission',
    loading: 'Loading coverage requests…',
    intro:
      'Ask another crew member to cover your place on one assigned visit, or volunteer to cover theirs. A manager approves each replacement. The visit time and the rest of the crew stay the same.',
    delivery:
      'Check this screen and your in-app notifications for updates. Push notifications are not available yet. These actions send no client message.',
    visit: 'Assigned visit',
    choose: 'Choose a visit',
    note: 'Coverage note (optional)',
    submit: 'Request cover',
    emptyVisits: 'No upcoming assigned visits are available to request cover for.',
    truncated: 'Only the first 100 visits are shown.',
    empty: 'No coverage requests are visible here.',
    own: 'Your request',
    former: 'Former team member',
    urgent: 'Within 24 hours: manager review only',
    stale: 'This visit changed. Cancel or decline this request and review the current visit.',
    open: 'Open',
    claimed: 'Awaiting manager approval',
    approved: 'Approved',
    declined: 'Declined',
    cancelled: 'Cancelled',
    claim: 'Volunteer to cover',
    cancel: 'Cancel request',
    claimedBy: 'Volunteered by',
    review: 'Review replacement people',
    replacement: 'Replacement',
    allow: 'Also allow these people to volunteer',
    saveAudience: 'Save additional audience',
    peopleTruncated: 'Only the first 200 people are shown.',
    approve: 'Approve replacement',
    decline: 'Decline request',
    saved: 'Saved. Refreshing coverage requests.',
    approval: 'Volunteering does not change assignments. Approval changes this visit only.',
    availability:
      'An assigned person is unavailable. Review the current hours before explicitly confirming this exception.',
    confirm: 'Confirm availability exception and approve',
    failed: 'The result could not be confirmed. Retry the same submission before making another change.',
    readFailed: 'Coverage requests could not be loaded. Refresh to retry.',
    conflict: 'The visit or coverage details changed. Review the current requests before trying again.',
    invalid: 'Review the selected visit or replacement and try again.',
    unavailable: 'Visit coverage is unavailable for this account and location.',
    offline: 'Connect before submitting. Your draft stays here while this page is open.',
    more: 'Load more',
  },
  es: {
    title: 'Cobertura de visitas',
    back: 'Personal',
    refresh: 'Actualizar',
    retry: 'Reintentar envío',
    loading: 'Cargando solicitudes de cobertura…',
    intro:
      'Pida a otro integrante que cubra su lugar en una visita asignada, u ofrézcase a cubrir el suyo. Un gerente aprueba cada reemplazo. La hora de la visita y el resto del equipo se mantienen.',
    delivery:
      'Revise esta pantalla y las notificaciones de la aplicación para ver las novedades. Las notificaciones push aún no están disponibles. Estas acciones no envían mensajes al cliente.',
    visit: 'Visita asignada',
    choose: 'Elija una visita',
    note: 'Nota de cobertura (opcional)',
    submit: 'Solicitar cobertura',
    emptyVisits: 'No hay visitas asignadas próximas para solicitar cobertura.',
    truncated: 'Solo se muestran las primeras 100 visitas.',
    empty: 'No hay solicitudes de cobertura visibles aquí.',
    own: 'Su solicitud',
    former: 'Exintegrante del equipo',
    urgent: 'Dentro de 24 horas: revisión exclusiva del gerente',
    stale: 'Esta visita cambió. Cancele o rechace la solicitud y revise la visita actual.',
    open: 'Abierta',
    claimed: 'Pendiente de aprobación del gerente',
    approved: 'Aprobada',
    declined: 'Rechazada',
    cancelled: 'Cancelada',
    claim: 'Ofrecerse a cubrir',
    cancel: 'Cancelar solicitud',
    claimedBy: 'Se ofreció a cubrir',
    review: 'Revisar personas de reemplazo',
    replacement: 'Reemplazo',
    allow: 'Permitir también que estas personas se ofrezcan a cubrir',
    saveAudience: 'Guardar destinatarios adicionales',
    peopleTruncated: 'Solo se muestran las primeras 200 personas.',
    approve: 'Aprobar reemplazo',
    decline: 'Rechazar solicitud',
    saved: 'Guardado. Actualizando las solicitudes de cobertura.',
    approval: 'Ofrecerse a cubrir no cambia las asignaciones. La aprobación cambia solo esta visita.',
    availability:
      'Una persona asignada no está disponible. Revise el horario actual antes de confirmar explícitamente esta excepción.',
    confirm: 'Confirmar excepción de disponibilidad y aprobar',
    failed: 'No se pudo confirmar el resultado. Reintente el mismo envío antes de realizar otro cambio.',
    readFailed: 'No se pudieron cargar las solicitudes de cobertura. Actualice para reintentarlo.',
    conflict: 'La visita o los detalles de cobertura cambiaron. Revise las solicitudes actuales antes de reintentarlo.',
    invalid: 'Revise la visita o el reemplazo seleccionado y vuelva a intentarlo.',
    unavailable: 'La cobertura de visitas no está disponible para esta cuenta y ubicación.',
    offline: 'Conéctese antes de enviar. Su borrador permanece aquí mientras esta página esté abierta.',
    more: 'Cargar más',
  },
} as const;
type People = {
  items: { id: string; name: string; eligible: number; explicitlyAllowed: number }[];
  truncated: boolean;
};
type Submission = { path: string; body: Record<string, unknown>; creation: boolean };
const button = 'btn btn-secondary min-h-11 whitespace-normal disabled:opacity-50';
const input = 'field-control block w-full min-w-0';
export default function StaffVisitOffersPage() {
  const s = useSession();
  return (
    <Content
      key={`${s.user?.companyId}:${s.user?.id}:${s.currentLocation?.id}:${s.user?.role}:${s.user?.permissions?.join(',')}:${s.company?.planFeatures?.join(',')}`}
    />
  );
}
function Content() {
  const { i18n } = useTranslation(),
    copy = visitOfferCopy[i18n.language.startsWith('es') ? 'es' : 'en'];
  const [list, setList] = useState<VisitOfferListing | null>(null),
    [visits, setVisits] = useState<OfferableVisit[]>([]),
    [truncated, setTruncated] = useState(false);
  const [visitId, setVisitId] = useState(''),
    [note, setNote] = useState(''),
    [people, setPeople] = useState<Record<string, People>>({}),
    [replacements, setReplacements] = useState<Record<string, string>>({}),
    [audiences, setAudiences] = useState<Record<string, string[]>>({});
  const [error, setError] = useState(''),
    [message, setMessage] = useState(''),
    [busy, setBusy] = useState(false),
    [uncertain, setUncertain] = useState(false),
    [warning, setWarning] = useState<Submission | null>(null);
  const alive = useRef(true),
    generation = useRef(0),
    lock = useRef(false),
    pending = useRef<Submission | null>(null);
  useEffect(() => {
    alive.current = true;
    void refresh();
    return () => {
      alive.current = false;
      generation.current++;
    };
  }, []);
  async function refresh(more = false) {
    if (lock.current && !more) return;
    const gen = ++generation.current;
    try {
      const [data, jobs] = await Promise.all([
        api.get<VisitOfferListing>(
          `/staff/visit-offers${more && list?.nextCursor ? `?before=${encodeURIComponent(list.nextCursor)}` : ''}`,
        ),
        api.get<{ items: OfferableVisit[]; truncated: boolean }>('/staff/visit-offers/visits'),
      ]);
      if (!alive.current || gen !== generation.current) return;
      setList((old) => (more && old ? { ...data, items: [...old.items, ...data.items] } : data));
      setVisits(jobs.items);
      setTruncated(jobs.truncated);
      setPeople({});
      setAudiences({});
      setReplacements({});
    } catch (e) {
      if (!alive.current || gen !== generation.current) return;
      const status = (e as { status?: number }).status;
      if ([401, 402, 403, 404].includes(status ?? 0)) {
        setList(null);
        setVisits([]);
        setPeople({});
        setAudiences({});
        setReplacements({});
      }
      setError([401, 402, 403, 404].includes(status ?? 0) ? copy.unavailable : copy.readFailed);
    }
  }
  async function mutate(path: string, body: Record<string, unknown>, creation = false) {
    if (lock.current || (pending.current && pending.current.path !== path)) return;
    if (!navigator.onLine) {
      setError(copy.offline);
      return;
    }
    lock.current = true;
    setBusy(true);
    setError('');
    setMessage('');
    setWarning(null);
    pending.current ??= { path, body, creation };
    try {
      await api.post(pending.current.path, pending.current.body);
      if (!alive.current) return;
      if (pending.current.creation) {
        setNote('');
        setVisitId('');
      }
      pending.current = null;
      setUncertain(false);
      setMessage(copy.saved);
      lock.current = false;
      await refresh();
    } catch (e) {
      if (!alive.current) return;
      const err = e as { status?: number; code?: string; fields?: Record<string, string> };
      if (err.status && err.status >= 400 && err.status < 500) {
        pending.current = null;
        setUncertain(false);
        if ([401, 402, 403, 404].includes(err.status)) {
          generation.current++;
          setList(null);
          setVisits([]);
          setPeople({});
          setAudiences({});
          setReplacements({});
        }
        if (
          err.code === 'AVAILABILITY_CONFIRMATION_REQUIRED' &&
          path.endsWith('/decision') &&
          err.fields?.availabilityConfirmation
        ) {
          setWarning({
            path,
            body: {
              ...body,
              clientMutationId: crypto.randomUUID(),
              availabilityConfirmation: err.fields.availabilityConfirmation,
            },
            creation: false,
          });
          setError(copy.availability);
        } else {
          setError(err.status === 409 ? copy.conflict : err.status === 400 ? copy.invalid : copy.unavailable);
          lock.current = false;
          void refresh();
        }
      } else {
        setUncertain(true);
        setError(copy.failed);
      }
    } finally {
      lock.current = false;
      if (alive.current) setBusy(false);
    }
  }
  async function review(row: VisitOfferItem) {
    const gen = generation.current;
    try {
      const result = await api.get<People>(`/staff/visit-offers/${row.id}/people`);
      if (!alive.current || gen !== generation.current) return;
      setPeople((old) => ({ ...old, [row.id]: result }));
      setAudiences((old) => ({ ...old, [row.id]: result.items.filter((p) => p.explicitlyAllowed).map((p) => p.id) }));
    } catch (e) {
      if (alive.current && gen === generation.current) {
        const denied = [401, 402, 403, 404].includes((e as { status?: number }).status ?? 0);
        if (denied) {
          generation.current++;
          setList(null);
          setVisits([]);
          setPeople({});
          setAudiences({});
          setReplacements({});
        }
        setError(denied ? copy.unavailable : copy.readFailed);
      }
    }
  }
  const disabled = busy || uncertain;
  const action = (row: VisitOfferItem) => ({ clientMutationId: crypto.randomUUID(), expectedRevision: row.revision });
  const label = (v: { clientName: string; startsAt: string; timezone: string }) =>
    `${v.clientName} · ${new Date(v.startsAt).toLocaleString(i18n.language, { timeZone: v.timezone })} (${v.timezone})`;
  return (
    <WorkPage
      title={copy.title}
      description={copy.intro}
      actions={
        <button
          className={button}
          disabled={busy}
          onClick={() => {
            setError('');
            setWarning(null);
            void refresh();
          }}
        >
          {copy.refresh}
        </button>
      }
    >
      <p className="text-sm text-[color:var(--color-muted)]">{copy.delivery}</p>
      {error && <Alert tone="error">{error}</Alert>}
      {message && <Alert tone="success">{message}</Alert>}
      {uncertain && (
        <button
          className={button}
          disabled={busy}
          onClick={() =>
            pending.current && void mutate(pending.current.path, pending.current.body, pending.current.creation)
          }
        >
          {copy.retry}
        </button>
      )}
      {warning && (
        <button className={button} disabled={disabled} onClick={() => void mutate(warning.path, warning.body)}>
          {copy.confirm}
        </button>
      )}
      {!list && !error && <LoadingBlock label={copy.loading} />}
      {list && (
        <>
          {list.canWrite && (
            <form
              className="card space-y-3"
              onSubmit={(e) => {
                e.preventDefault();
                const v = visits.find((v) => v.id === visitId);
                if (v)
                  void mutate(
                    '/staff/visit-offers',
                    {
                      clientMutationId: crypto.randomUUID(),
                      appointmentId: v.id,
                      expectedAppointmentVersion: v.version,
                      note,
                    },
                    true,
                  );
              }}
            >
              <h2 className="text-lg font-semibold">
                {i18n.language.startsWith('es') ? 'Solicitar cobertura de una visita' : 'Request cover for a visit'}
              </h2>
              <fieldset disabled={disabled} className="space-y-3">
                <label className="block" htmlFor="offer-visit">
                  {copy.visit}
                </label>
                <select
                  id="offer-visit"
                  className={input}
                  required
                  value={visitId}
                  onChange={(e) => setVisitId(e.target.value)}
                >
                  <option value="">{copy.choose}</option>
                  {visits.map((v) => (
                    <option key={v.id} value={v.id}>
                      {label(v)}
                    </option>
                  ))}
                </select>
                {!visits.length && <p>{copy.emptyVisits}</p>}
                {truncated && <p>{copy.truncated}</p>}
                <label className="block" htmlFor="offer-note">
                  {copy.note}
                </label>
                <textarea
                  id="offer-note"
                  className={input}
                  maxLength={1000}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                />
                <button className={button} disabled={!visits.length} type="submit">
                  {copy.submit}
                </button>
              </fieldset>
            </form>
          )}
          <WorkSection title={i18n.language.startsWith('es') ? 'Solicitudes de cobertura' : 'Coverage requests'}>
            {!list.items.length && !error && <WorkEmpty title={copy.empty} />}
            {list.items.map((row) => (
              <article key={row.id} className="work-row mb-3 min-w-0 space-y-3">
                <h3 className="font-bold break-words">{row.own ? copy.own : (row.offeringName ?? copy.former)}</h3>
                <p className="break-words">{label(row)}</p>
                <p>{copy[row.status]}</p>
                <p>{copy.approval}</p>
                {row.urgent && <p>{copy.urgent}</p>}
                {row.stale && <p>{copy.stale}</p>}
                {row.note && <p className="whitespace-pre-wrap break-words">{row.note}</p>}
                {row.claimantId && (
                  <p>
                    {copy.claimedBy}: {row.claimantName ?? copy.former}
                  </p>
                )}
                <div className="flex flex-wrap gap-3">
                  {row.canClaim && (
                    <button
                      className={button}
                      disabled={disabled}
                      onClick={() => void mutate(`/staff/visit-offers/${row.id}/claim`, action(row))}
                    >
                      {copy.claim}
                    </button>
                  )}
                  {row.own && list.canWrite && ['open', 'claimed'].includes(row.status) && (
                    <button
                      className={button}
                      disabled={disabled}
                      onClick={() => void mutate(`/staff/visit-offers/${row.id}/cancel`, action(row))}
                    >
                      {copy.cancel}
                    </button>
                  )}
                </div>
                {list.canManage && ['open', 'claimed'].includes(row.status) && (
                  <fieldset disabled={disabled} className="space-y-3">
                    <button className={button} onClick={() => void review(row)}>
                      {copy.review}
                    </button>
                    {people[row.id] && (
                      <>
                        {people[row.id]!.truncated && <p>{copy.peopleTruncated}</p>}
                        <label className="block" htmlFor={`replacement-${row.id}`}>
                          {copy.replacement}
                        </label>
                        <select
                          id={`replacement-${row.id}`}
                          className={input}
                          value={row.claimantId ?? replacements[row.id] ?? ''}
                          disabled={!!row.claimantId}
                          onChange={(e) => setReplacements((old) => ({ ...old, [row.id]: e.target.value }))}
                        >
                          <option value="">{copy.choose}</option>
                          {people[row.id]!.items.map((p) => (
                            <option key={p.id} value={p.id}>
                              {p.name}
                            </option>
                          ))}
                        </select>
                        {row.status === 'open' && !row.urgent && !row.stale && (
                          <fieldset className="space-y-2">
                            <legend>{copy.allow}</legend>
                            {people[row.id]!.items.filter((p) => !p.eligible || p.explicitlyAllowed).map((p) => (
                              <label key={p.id} className="flex min-h-11 items-center gap-2">
                                <input
                                  type="checkbox"
                                  checked={audiences[row.id]?.includes(p.id) ?? false}
                                  onChange={(e) =>
                                    setAudiences((old) => ({
                                      ...old,
                                      [row.id]: e.target.checked
                                        ? [...(old[row.id] ?? []), p.id]
                                        : (old[row.id] ?? []).filter((id) => id !== p.id),
                                    }))
                                  }
                                />
                                {p.name}
                              </label>
                            ))}
                            <button
                              className={button}
                              onClick={() =>
                                void mutate(`/staff/visit-offers/${row.id}/audience`, {
                                  ...action(row),
                                  memberIds: audiences[row.id] ?? [],
                                })
                              }
                            >
                              {copy.saveAudience}
                            </button>
                          </fieldset>
                        )}
                      </>
                    )}
                    <div className="flex flex-wrap gap-3">
                      <button
                        className={button}
                        disabled={row.stale || !people[row.id] || (!row.claimantId && !replacements[row.id])}
                        onClick={() =>
                          void mutate(`/staff/visit-offers/${row.id}/decision`, {
                            ...action(row),
                            decision: 'approved',
                            expectedAppointmentVersion: row.currentAppointmentVersion,
                            replacementUserId: row.claimantId ?? replacements[row.id],
                          })
                        }
                      >
                        {copy.approve}
                      </button>
                      <button
                        className={button}
                        onClick={() =>
                          void mutate(`/staff/visit-offers/${row.id}/decision`, {
                            ...action(row),
                            decision: 'declined',
                            expectedAppointmentVersion: row.currentAppointmentVersion,
                          })
                        }
                      >
                        {copy.decline}
                      </button>
                    </div>
                  </fieldset>
                )}
              </article>
            ))}
            {list.nextCursor && (
              <button className={button} disabled={disabled} onClick={() => void refresh(true)}>
                {copy.more}
              </button>
            )}
          </WorkSection>
        </>
      )}
    </WorkPage>
  );
}
